import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Search } from 'lucide-react';
import SiteSearch from './SiteSearch';
import { useAnalytics } from '../hooks/useAnalytics';
import EnhancedPhoneButton from './EnhancedPhoneButton';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { trackEvent } = useAnalytics();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Builders', href: '/builders' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial scroll position
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleSearchOpen = () => {
    setSearchOpen(true);
    trackEvent({
      category: 'Navigation',
      action: 'Search Open'
    });
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-primary shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-20">
          {/* Call Button - Mobile Left */}
          <div className="flex-shrink-0 sm:hidden">
            <EnhancedPhoneButton
              variant="primary"
              size="sm"
              trackingLabel="Mobile Header Phone Button"
            >
              Call
            </EnhancedPhoneButton>
          </div>

          {/* Logo and Brand - Centered on mobile, Left on desktop */}
          <div className="flex-1 flex justify-center lg:justify-start lg:w-1/4">
            <Link to="/" className="flex items-center">
              {/* Desktop Logo (md and up) */}
              <div className="hidden md:flex items-center space-x-3">
                <img 
                  src="https://i.imgur.com/jsSwFcY.jpg" 
                  alt="Pinnacle Home Theater Logo" 
                  className="h-11 w-auto rounded object-contain"
                />
                <img 
                  src="https://i.imgur.com/IgVP32C.jpg"
                  alt="Pinnacle"
                  className="h-14 object-contain"
                />
              </div>
              
              {/* Mobile Logo (sm and down) */}
              <div className="md:hidden">
                <img 
                  src="https://i.imgur.com/H2WSFGE.jpg"
                  alt="Pinnacle Home Theater"
                  className="h-14 w-auto object-contain"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex flex-1 lg:w-1/2 justify-center">
            <div className="flex items-center justify-center space-x-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    isActive(item.href)
                      ? 'text-secondary font-semibold'
                      : 'text-gray-100 hover:text-secondary'
                  } transition-colors duration-200 font-medium text-base whitespace-nowrap`}
                >
                  {item.name}
                </Link>
              ))}
              <button
                onClick={handleSearchOpen}
                className="text-gray-100 hover:text-secondary transition-colors duration-200 p-2"
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Phone Number - Desktop Right */}
          <div className="hidden lg:flex flex-1 lg:w-1/4 justify-end">
            <EnhancedPhoneButton
              variant="primary"
              size="md"
              trackingLabel="Header Phone Button"
              className="whitespace-nowrap"
            />
          </div>

          {/* Mobile Menu Button - Right */}
          <div className="lg:hidden flex-shrink-0">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white hover:text-secondary focus:outline-none p-2"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div 
        className={`
          lg:hidden fixed inset-x-0 top-20 bottom-0 transform transition-transform duration-300 ease-in-out z-50
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <div className="bg-primary shadow-xl h-full">
          <div className="px-4 py-6 space-y-2">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`${
                isActive(item.href)
                  ? 'text-secondary bg-white/10'
                  : 'text-white hover:text-secondary hover:bg-white/5'
              } block py-4 px-4 text-lg font-medium rounded-lg transition-colors ${
                isActive(item.href) ? 'font-semibold' : ''
              }`}
              onClick={() => setIsOpen(false)}
            >
              {item.name}
            </Link>
          ))}
            
            <button
              onClick={() => {
                handleSearchOpen();
                setIsOpen(false);
              }}
              className="flex items-center py-4 px-4 text-lg font-medium text-white hover:text-secondary hover:bg-white/5 rounded-lg transition-colors w-full"
            >
              <Search className="w-5 h-5 mr-3" />
              Search
            </button>
            
            <div className="pt-4 mt-4 border-t border-white/20">
              <EnhancedPhoneButton
                variant="secondary"
                size="lg"
                trackingLabel="Mobile Menu Phone Button"
                className="w-full justify-center"
              />
            </div>
          </div>
        </div>
      </div>
      </nav>
      
      <SiteSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Navbar;